export interface ScrollToElementAndExecuteOptions {
  /**
   * 滾動完成後要執行的 callback
   */
  onComplete?: () => void
  /**
   * 是否為手機版，會影響滾動行為
   * - 手機版：滾動到元素頂部距離視窗頂部 60px 處
   * - 桌面版：滾動到元素置中
   */
  isMobile?: boolean
  /**
   * 手機版時，元素頂部距離視窗頂部的偏移量
   * @default 20
   */
  mobileTopOffset?: number
  /**
   * 桌面版時，判斷是否需要滾動的閾值（元素中心距離視窗中心的距離）
   * @default 50
   */
  desktopScrollThreshold?: number
}

/**
 * 取得 iOS safe area inset top 的值
 * @returns safe area inset top 的像素值
 */
const getSafeAreaInsetTop = (): number => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return 0
  }

  // 使用 CSS.supports 檢查是否支援 env()
  if (!CSS.supports('padding-top', 'env(safe-area-inset-top)')) {
    return 0
  }

  // 創建臨時元素來讀取 safe area 值
  const tempElement = document.createElement('div')
  tempElement.style.paddingTop = 'env(safe-area-inset-top)'
  tempElement.style.position = 'fixed'
  tempElement.style.visibility = 'hidden'
  document.body.appendChild(tempElement)

  const safeAreaTop = parseInt(getComputedStyle(tempElement).paddingTop, 10) || 0

  document.body.removeChild(tempElement)

  return safeAreaTop
}

/**
 * 等待滾動結束後執行 callback
 * 使用 scroll 事件 + debounce 作為 fallback，解決 iOS Safari scrollend 事件不穩定的問題
 */
const waitForScrollEnd = (callback: () => void, timeout = 150) => {
  let scrollTimeout: ReturnType<typeof setTimeout> | null = null
  let hasExecuted = false

  const executeOnce = () => {
    if (hasExecuted) return
    hasExecuted = true
    window.removeEventListener('scroll', handleScroll)
    callback()
  }

  const handleScroll = () => {
    if (scrollTimeout) {
      clearTimeout(scrollTimeout)
    }
    scrollTimeout = setTimeout(executeOnce, timeout)
  }

  // 監聽 scroll 事件，當停止滾動一段時間後執行
  window.addEventListener('scroll', handleScroll, { passive: true })

  // 設置最大等待時間，防止 callback 永遠不執行
  setTimeout(() => {
    if (scrollTimeout) {
      clearTimeout(scrollTimeout)
    }
    executeOnce()
  }, 1000)

  // 立即開始計時，處理已經在目標位置的情況
  scrollTimeout = setTimeout(executeOnce, timeout)
}

/**
 * 滾動到指定元素後執行 callback
 *
 * 此函數會先判斷是否需要滾動：
 * - 如果元素已經在視窗內適當位置，直接執行 callback
 * - 如果需要滾動，會等待滾動完成後再執行 callback
 * - 如果頁面已經到底且需要向下滾動，直接執行 callback（無法滾動）
 *
 * @param element - 要滾動到的目標元素
 * @param options - 滾動選項
 */
export const scrollToElementAndExecute = (
  element: HTMLElement | null,
  options: ScrollToElementAndExecuteOptions = {},
) => {
  const {
    onComplete,
    isMobile = false,
    mobileTopOffset = 20,
    desktopScrollThreshold = 50,
  } = options

  if (!element) {
    onComplete?.()
    return
  }

  const rect = element.getBoundingClientRect()
  const viewportHeight = window.innerHeight
  const maxScrollTop = document.documentElement.scrollHeight - viewportHeight
  const isAtBottom = Math.abs(window.scrollY - maxScrollTop) < 1

  const executeCallback = () => onComplete?.()

  if (isMobile) {
    const safeAreaInsetTop = getSafeAreaInsetTop()
    const targetTop = rect.top + window.scrollY - mobileTopOffset - safeAreaInsetTop
    const needsScrollDown = targetTop > window.scrollY
    const needsScroll = Math.abs(window.scrollY - targetTop) > 1
    // 如果需要往下滾但已經到底，無法滾動
    const canScroll = needsScroll && !(needsScrollDown && isAtBottom)

    if (canScroll) {
      window.scrollTo({ top: Math.min(targetTop, maxScrollTop), behavior: 'smooth' })
      waitForScrollEnd(executeCallback)
    }
    else {
      executeCallback()
    }
  }
  else {
    // 檢查元素是否已經接近視窗中央
    const elementCenter = rect.top + rect.height / 2
    const viewportCenter = viewportHeight / 2
    const needsScrollDown = elementCenter > viewportCenter
    const needsScroll = Math.abs(elementCenter - viewportCenter) > desktopScrollThreshold
    // 如果需要往下滾但已經到底，無法滾動
    const canScroll = needsScroll && !(needsScrollDown && isAtBottom)

    if (canScroll) {
      element.scrollIntoView({ block: 'center', behavior: 'smooth' })
      waitForScrollEnd(executeCallback)
    }
    else {
      executeCallback()
    }
  }
}
